import { type Cadre, CADRE_ENTIER, cadreEntier, cadreValide } from '@admin-pmvb/core'
import { type KeyboardEvent, type PointerEvent, useRef, useState } from 'react'
import { Icone } from './Icone'

// Recadrage d'une page photographiée, plein écran : la photo, la page trouvée (bords
// détectés) et ses quatre coins, que l'on déplace du doigt (une loupe montre le coin sous le
// doigt) ou au clavier. « Valider » garde ces coins, « Garder la photo entière » les oublie.

const NOMS = ['haut gauche', 'haut droit', 'bas droit', 'bas gauche'] as const
/** Grossissement et rayon (px) de la loupe. */
const LOUPE_ZOOM = 3
const LOUPE_RAYON = 48

type Coins = [number, number, number, number, number, number, number, number]

export function RecadragePage({
  photo,
  cadre,
  proposition,
  recherche,
  onValider,
  onFermer,
}: {
  /** URL de la photo, telle qu'affichée. */
  photo: string
  /** Coins retenus jusqu'ici ; null : la photo entière. */
  cadre: Cadre | null
  /** Bords détectés ; null : pas trouvés. */
  proposition: Cadre | null
  /** Bords encore cherchés. */
  recherche: boolean
  onValider: (cadre: Cadre | null) => void
  onFermer: () => void
}) {
  const [coins, setCoins] = useState<Coins>(
    () => [...(cadre ?? proposition ?? CADRE_ENTIER)] as Coins,
  )
  const [prise, setPrise] = useState<number | null>(null)
  const zone = useRef<HTMLDivElement>(null)
  const [taille, setTaille] = useState({ l: 0, h: 0 })
  const courant = coins as Cadre
  const valide = cadreValide(courant)

  const placer = (k: number, x: number, y: number) =>
    setCoins((c) => {
      const n = [...c] as Coins
      n[2 * k] = Math.min(1, Math.max(0, x))
      n[2 * k + 1] = Math.min(1, Math.max(0, y))
      return n
    })

  const suivre = (k: number, e: PointerEvent) => {
    const r = zone.current?.getBoundingClientRect()
    if (!r || r.width === 0 || r.height === 0) return
    setTaille({ l: r.width, h: r.height })
    placer(k, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height)
  }

  const clavier = (k: number, e: KeyboardEvent) => {
    const pas = e.shiftKey ? 0.02 : 0.005
    const d = {
      ArrowLeft: [-pas, 0],
      ArrowRight: [pas, 0],
      ArrowUp: [0, -pas],
      ArrowDown: [0, pas],
    }[e.key]
    if (!d) return
    e.preventDefault()
    placer(k, coins[2 * k]! + d[0]!, coins[2 * k + 1]! + d[1]!)
  }

  const points = [0, 1, 2, 3].map((k) => `${coins[2 * k]} ${coins[2 * k + 1]}`)
  const message = recherche
    ? 'Recherche des bords de la page…'
    : proposition
      ? 'Bords de la page trouvés. Ajustez les coins si besoin, puis validez.'
      : 'Bords non trouvés : placez les quatre coins sur ceux de la page, ou gardez la photo entière.'

  return (
    <div
      className="defi-ecran recadrage"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recadrage-titre"
    >
      <header className="defi-entete">
        <div>
          <p className="defi-surtitre">Photographier un document</p>
          <h1 id="recadrage-titre">Recadrer la page</h1>
        </div>
        <button
          type="button"
          className="bouton-icone"
          onClick={onFermer}
          aria-label="Fermer sans changer"
          title="Fermer sans changer"
        >
          <Icone nom="close" />
        </button>
      </header>
      <p className="discret recadrage-message" aria-live="polite">
        {message}
      </p>
      <div className="recadrage-cadre">
        <div className="recadrage-zone" ref={zone}>
          <img src={photo} alt="Photo de la page" draggable={false} />
          {!recherche && (
            <>
              <svg
                className="recadrage-masque"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d={`M0 0H1V1H0Z M${points.join('L')}Z`} fillRule="evenodd" />
                <polygon points={points.join(' ')} vectorEffect="non-scaling-stroke" />
              </svg>
              {NOMS.map((nom, k) => (
                <button
                  key={nom}
                  type="button"
                  className="recadrage-coin"
                  style={{ left: `${coins[2 * k]! * 100}%`, top: `${coins[2 * k + 1]! * 100}%` }}
                  aria-label={`Coin ${nom} : glisser, ou flèches du clavier`}
                  onPointerDown={(e) => {
                    e.currentTarget.setPointerCapture(e.pointerId)
                    setPrise(k)
                    suivre(k, e)
                  }}
                  onPointerMove={(e) => {
                    if (prise === k) suivre(k, e)
                  }}
                  onPointerUp={() => setPrise(null)}
                  onPointerCancel={() => setPrise(null)}
                  onKeyDown={(e) => clavier(k, e)}
                />
              ))}
              {prise !== null && taille.l > 0 && (
                <div
                  className={
                    coins[2 * prise + 1]! < 0.3
                      ? 'recadrage-loupe recadrage-loupe-dessous'
                      : 'recadrage-loupe'
                  }
                  aria-hidden="true"
                  style={{
                    left: `${coins[2 * prise]! * 100}%`,
                    top: `${coins[2 * prise + 1]! * 100}%`,
                    width: LOUPE_RAYON * 2,
                    height: LOUPE_RAYON * 2,
                    backgroundImage: `url("${photo}")`,
                    backgroundSize: `${taille.l * LOUPE_ZOOM}px ${taille.h * LOUPE_ZOOM}px`,
                    backgroundPosition: `${LOUPE_RAYON - coins[2 * prise]! * taille.l * LOUPE_ZOOM}px ${
                      LOUPE_RAYON - coins[2 * prise + 1]! * taille.h * LOUPE_ZOOM
                    }px`,
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
      {!recherche && !valide && (
        <p className="alerte">Les coins se croisent ou la page est trop petite : déplacez-les.</p>
      )}
      <div className="recadrage-boutons">
        <button
          type="button"
          className="bouton bouton-grand"
          disabled={recherche || !valide}
          onClick={() => onValider(cadreEntier(courant) ? null : courant)}
        >
          Valider
        </button>
        <button type="button" className="bouton-discret" onClick={() => onValider(null)}>
          Garder la photo entière
        </button>
        {proposition && !recherche && (
          <button
            type="button"
            className="bouton-discret"
            onClick={() => setCoins([...proposition] as Coins)}
          >
            Revenir aux bords trouvés
          </button>
        )}
      </div>
    </div>
  )
}
